// client/src/services/lexistarAnalytics.ts
//
// LexiStar analytics API client (ST-00188, M4-A; contracts docs/lexistar/contracts/
// m4-contracts.md §4; LS-03 FR-RPT-001/006, FR-STAT-007, FR-WB-012, FR-RPL-007, FR-AI-005).
// Server: server/modules/lexistar/analytics, mounted at /api/lexistar. Response shapes:
// src/types/lexistar-analytics.ts (a copy of server/types/lexistar-analytics.ts).
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
// header, single-flight 401 refresh; `api` already carries `/api`); every helper resolves to
// the response BODY. Dates are SAST days `YYYY-MM-DD` (toApiDateString from utils/dateUtils).
// List parameters (metrics, assets) take an array or a comma list.
//
//   getMetrics        GET /metrics                 the governed metric catalogue (the caller's)
//   getKpis           GET /kpis                    series per period + totals (+ previous, change)
//   getTopExceptions  GET /kpis/top-exceptions     "the top five things to look at"
//   getKpiBreakdown   GET /kpis/breakdown          one metric by route | customer | material | …
//                                                  (within a scope; series=true adds each row's trend)
//   getTonnage        GET /tonnage                 FR-WB-012 summaries (receipt, else dispatch)
//   getDwell          GET /dwell                   per-site dwell distributions + exceedances
//   listDwellLimits / createDwellLimit / updateDwellLimit   /dwell-limits (PATCH takes `version`)
//   getIdleHeatmap    GET /idle-heatmap            idle cells + ranked hotspots (litres, rand);
//                                                  filters: assets, routes, drivers, exclude_permitted
//   getSpeeding       GET /speeding                overspeed episodes per route | truck | driver
//                                                  (count, time over, worst excess, per 100 km; ST-00256)
//   getSpeedingEvents GET /speeding/events         the episodes behind a speeding row (newest first)
//
// The metric routes answer 503 until migration 028 is applied and while the seeded catalogue
// and the server's metric pack disagree (the body names the difference).

import api from './api';
import type {
  AggregateGrain,
  AggregateScopeType,
  BreakdownBy,
  BreakdownResponse,
  DwellLimit,
  DwellLimitInput,
  DwellLimitsResponse,
  DwellResponse,
  IdleHeatmapResponse,
  KpisResponse,
  MetricsCatalogueResponse,
  SpeedingBy,
  SpeedingEventsResponse,
  SpeedingResponse,
  TonnageBy,
  TonnageResponse,
  TopExceptionsResponse,
} from '../types/lexistar-analytics';

const LX = '/lexistar';

type Scalar = string | number | boolean | null | undefined;
type ListParam = string | string[] | null | undefined;

/** Drop undefined / null / '' and join arrays with commas, so the API never sees `metrics=`. */
function query(
  params: Record<string, Scalar | ListParam> = {}
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
      continue;
    }
    out[k] = v;
  }
  return out;
}

export interface MetricsQuery {
  family?: string;
  scope?: AggregateScopeType | 'trip';
}

export const getMetrics = (params: MetricsQuery = {}): Promise<MetricsCatalogueResponse> =>
  api
    .get<MetricsCatalogueResponse>(`${LX}/metrics`, { params: query({ ...params }) })
    .then((r) => r.data);

export interface KpisQuery {
  scope_type?: AggregateScopeType;
  scope_id?: string | number;
  grain?: AggregateGrain;
  from?: string;
  to?: string;
  metrics?: ListParam;
  compare?: 'previous' | 'none';
  /** Global admins only: another organisation's figures. */
  tenant_id?: number;
}

export const getKpis = (params: KpisQuery = {}): Promise<KpisResponse> =>
  api.get<KpisResponse>(`${LX}/kpis`, { params: query({ ...params }) }).then((r) => r.data);

export interface TopExceptionsQuery {
  date?: string;
  limit?: number;
  tenant_id?: number;
}

/** `date` (SAST day, default today) or the full query. */
export const getTopExceptions = (
  params: TopExceptionsQuery | string = {}
): Promise<TopExceptionsResponse> =>
  api
    .get<TopExceptionsResponse>(`${LX}/kpis/top-exceptions`, {
      params: query(typeof params === 'string' ? { date: params } : { ...params }),
    })
    .then((r) => r.data);

export interface BreakdownQuery {
  metric: string;
  by: BreakdownBy;
  /** Narrow the rows to one scope (e.g. the routes of one customer); omitted = the whole operation. */
  scope_type?: AggregateScopeType;
  scope_id?: string | number;
  from?: string;
  to?: string;
  limit?: number;
  /** Add each row's trend (`rows[].series`) — the per-row sparkline without a request per row. */
  series?: boolean;
  tenant_id?: number;
}

export const getKpiBreakdown = (params: BreakdownQuery): Promise<BreakdownResponse> =>
  api
    .get<BreakdownResponse>(`${LX}/kpis/breakdown`, { params: query({ ...params }) })
    .then((r) => r.data);

export interface TonnageQuery {
  by?: TonnageBy;
  from?: string;
  to?: string;
  tenant_id?: number;
}

export const getTonnage = (params: TonnageQuery = {}): Promise<TonnageResponse> =>
  api.get<TonnageResponse>(`${LX}/tonnage`, { params: query({ ...params }) }).then((r) => r.data);

export interface DwellQuery {
  site?: number | string;
  from?: string;
  to?: string;
  tenant_id?: number;
}

export const getDwell = (params: DwellQuery = {}): Promise<DwellResponse> =>
  api.get<DwellResponse>(`${LX}/dwell`, { params: query({ ...params }) }).then((r) => r.data);

export const listDwellLimits = (
  params: { site?: number | string } = {}
): Promise<DwellLimitsResponse> =>
  api
    .get<DwellLimitsResponse>(`${LX}/dwell-limits`, { params: query({ ...params }) })
    .then((r) => r.data);

/** lexistar.trips.manage; 409 when the site already has a limit for the context. */
export const createDwellLimit = (body: DwellLimitInput): Promise<{ limit: DwellLimit }> =>
  api.post<{ limit: DwellLimit }>(`${LX}/dwell-limits`, body).then((r) => r.data);

/** lexistar.trips.manage; pass the `version` you read — 409 when someone changed it since. */
export const updateDwellLimit = (
  id: string,
  body: { max_min?: number; min_loading_min?: number | null; version?: number }
): Promise<{ limit: DwellLimit }> =>
  api.patch<{ limit: DwellLimit }>(`${LX}/dwell-limits/${id}`, body).then((r) => r.data);

export interface IdleHeatmapQuery {
  from?: string;
  to?: string;
  /** Truck ids, asset keys (asset:/reg:) or plates. */
  assets?: ListParam;
  /** Route ids: idle time on trips planned on these routes. */
  routes?: ListParam;
  /** Driver ids. */
  drivers?: ListParam;
  exclude_permitted?: boolean;
  /** Hotspots returned (default 20). */
  top?: number;
  tenant_id?: number;
}

export const getIdleHeatmap = (params: IdleHeatmapQuery = {}): Promise<IdleHeatmapResponse> =>
  api
    .get<IdleHeatmapResponse>(`${LX}/idle-heatmap`, { params: query({ ...params }) })
    .then((r) => r.data);

export interface SpeedingQuery {
  by?: SpeedingBy;
  from?: string;
  to?: string;
  /** The series buckets; omitted = days up to 92 days, weeks up to 400, else months. */
  grain?: AggregateGrain;
  tenant_id?: number;
}

/** ST-00256: by=driver needs lexistar.drivers.view as well (403 otherwise). */
export const getSpeeding = (params: SpeedingQuery = {}): Promise<SpeedingResponse> =>
  api.get<SpeedingResponse>(`${LX}/speeding`, { params: query({ ...params }) }).then((r) => r.data);

export interface SpeedingEventsQuery {
  by?: SpeedingBy;
  /** A GET /speeding row's scope_id (needs `by`); omitted = every episode of the window. */
  scope_id?: string;
  from?: string;
  to?: string;
  /** 1–1000, default 200. */
  limit?: number;
  tenant_id?: number;
}

export const getSpeedingEvents = (params: SpeedingEventsQuery = {}): Promise<SpeedingEventsResponse> =>
  api
    .get<SpeedingEventsResponse>(`${LX}/speeding/events`, { params: query({ ...params }) })
    .then((r) => r.data);
